import React, { useCallback, useEffect, useState } from 'react';
import { useThemeContext } from '../contexts/ThemeContext';
import { VISUAL_OPTIONS } from './visualOptions';

const HIDDEN_KEY = 'canvasVisualOptionsHidden';

const readHiddenPreference = () => {
  try {
    return window.localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Floating control so Will can A/B Studio / Blueprint / Signal while the UI is running.
 * Can be dismissed; preference persists in localStorage.
 */
const VisualOptionsPicker = () => {
  const { visualOptionId, setVisualOptionId } = useThemeContext();
  const [hidden, setHidden] = useState(readHiddenPreference);

  useEffect(() => {
    try {
      window.localStorage.setItem(HIDDEN_KEY, hidden ? '1' : '0');
    } catch {
      // Ignore private-mode / blocked storage.
    }
  }, [hidden]);

  const hide = useCallback(() => setHidden(true), []);
  const show = useCallback(() => setHidden(false), []);

  if (hidden) {
    return (
      <button
        type="button"
        className="canvas-visual-picker-fab"
        onClick={show}
        aria-label="Show visual options"
        title="Show visual options"
      >
        <span className="canvas-visual-picker-fab__swatch" aria-hidden>
          {(
            VISUAL_OPTIONS.find((o) => o.id === visualOptionId) ||
            VISUAL_OPTIONS[0]
          ).swatch.map((color) => (
            <span key={color} style={{ background: color }} />
          ))}
        </span>
        <span className="canvas-visual-picker-fab__label">Visual</span>
      </button>
    );
  }

  return (
    <aside className="canvas-visual-picker" aria-label="Canvas visual options">
      <div className="canvas-visual-picker__header">
        <p className="canvas-visual-picker__title">Visual options</p>
        <button
          type="button"
          className="canvas-visual-picker__hide"
          onClick={hide}
          aria-label="Hide visual options"
          title="Hide visual options"
        >
          Hide
        </button>
      </div>
      <div className="canvas-visual-picker__options" role="listbox">
        {VISUAL_OPTIONS.map((option) => {
          const active = option.id === visualOptionId;
          return (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={active}
              className={`canvas-visual-picker__btn${active ? ' is-active' : ''}`}
              onClick={() => setVisualOptionId(option.id)}
            >
              <span className="canvas-visual-picker__swatch" aria-hidden>
                {option.swatch.map((color) => (
                  <span key={color} style={{ background: color }} />
                ))}
              </span>
              <span className="canvas-visual-picker__meta">
                <span className="canvas-visual-picker__name">{option.name}</span>
                <span className="canvas-visual-picker__tagline">
                  {option.tagline}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="canvas-visual-picker__hint">
        Persists in localStorage · also try Lights On/Off in the nav
      </p>
    </aside>
  );
};

export default VisualOptionsPicker;
